import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { CF } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent
} from '@material-ui/core';
import { sortBy, uniq } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from '../../i18next';
import { useRoutes } from '../../routes';
import { useCurrentUser } from '../../services/currentUser';
import { callFirebaseFunction } from '../../services/firebaseFunctions';
import { useOrgsById } from '../../services/orgs';
import { ButtonWithPromise } from '../ButtonWithPromise';
import { DialogTitleWithTypography } from '../DialogTitleWithTypography';
import { Loader } from '../Loader';

const HEIGHT = 100;

const Row = styled('div')`
  display: grid;
  grid-template-columns: 3fr 2fr 1fr;
  grid-column-gap: ${(p) => p.theme.spacing(2)}px;
  align-items: center;
  margin-bottom: ${(p) => p.theme.spacing(2)}px;
`;

const OrganizationList = ({ orgs }: { orgs: Doc<IOrganization>[] }) => {
  const { t } = useTranslation();
  const { ROUTES } = useRoutes();
  const currentUser = useCurrentUser();
  const orgsWithDefaultAndSorted = useMemo(() => {
    return sortBy(
      orgs.map((org, i) => {
        return {
          org,
          default: i === 0
        };
      }),
      (o) => o.org.data.name
    );
  }, [orgs]);
  return (
    <>
      {orgsWithDefaultAndSorted.map((o) => (
        <Row key={o.org.id}>
          <div>{o.org.data.name}</div>
          {o.default ? (
            <Button disabled>
              <i>{t('Default')}</i>
            </Button>
          ) : (
            <ButtonWithPromise
              pending={`${t('Applying')}...`}
              onClick={() =>
                callFirebaseFunction(CF.adminUser.changeDefaultOrganization, {
                  userId: currentUser.id,
                  orgId: o.org.id
                })
              }
            >
              {t('Make default')}
            </ButtonWithPromise>
          )}
          <Button
            disabled={currentUser.org.id === o.org.id}
            variant="contained"
            color="primary"
            href={ROUTES.base.url(o.org.id)}
          >
            {t('Switch')}
          </Button>
        </Row>
      ))}
    </>
  );
};

export const SwitchOrganizationDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const orgIds = useMemo(() => uniq(currentUser.orgs), [currentUser.orgs]);
  const [orgs, loading] = useOrgsById(orgIds);
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithTypography>
        {t('Switch organization')}
      </DialogTitleWithTypography>
      <DialogContent>
        {loading && <Loader height={HEIGHT} />}

        {orgs && <OrganizationList orgs={orgs} />}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
